/* eslint-disable no-use-before-define */
import React, { useState, useEffect } from "react";
import { LoadingState } from "underwriter_dashboard/shared/types";
import { formatCurrency } from "@eave/shared/utils/currency";
import { validCurrencyInputsOnly } from "@eave/shared/utils";

import BaseTable, { EditingState } from "web_modules/shared/BaseTable";
import {
  Button,
  InlineNotification,
  Form,
  FormGroup,
  Loading,
  Modal,
  TextInput,
} from "carbon-components-react";
import { DocumentDownload16, Edit16 } from "@carbon/icons-react";
import APIClient from "web_modules/APIClient";

import Field from "./Field";

import { WS_NAME } from "../../shared/WorksheetEditor/wsDefs";
import {
  fetchWorksheets,
  getWsByName,
  getWsById,
  updateLoanFileWorksheet,
  deleteWSValue,
  updateWSValue,
} from "../../shared/WorksheetEditor/wsHelpers";
import { AppShape } from "@eave/shared/types/api";
import { CarbonInputGenerationParams } from "web_modules/shared/carbon-types";
import { initialValues } from "./requirements-form-values";
import { makeHeaderData } from "web_modules/shared/BaseTable/helpers";
import ReserveRequirements from "./ReserveRequirement";
import { notEmpty } from "underwriter_dashboard/shared/WorksheetEditor/wsValidationHelpers";
import BorrowerEntityUtil, { getBorrowerEntityAsString } from "@eave/shared/utils/borrowers";

const styles = require("./styles.module.scss");

interface Props {
  app: AppShape;
  borrowerEntity?: number;
  loadApp: Function;
  dispatch?: Function;

  setReserveTotal?: (reserveTotal: string) => void;
}

export interface ReserveRequirement {
  id: string;
  number_of_months: string; // num as string
  reason: string;
  version: string; // number as string

  // local metadata for constructing our requests, not a field on the struct
  array_index?: number;
}

interface Reserve {
  id: string;
  borrower_entity: string;
  pitia: string; // currency as string
  property: string;
  reserve_requirements: ReserveRequirement[];
  total_amount_of_reserves_required: string;
  total_months: string;
}

interface ReserveWorksheet {
  id: string;
  computed_total: string | null;
  operator_override: string | null;
  operator_override_reason: string | null;
  reserves: Reserve[];
  total: string | null;
  use_instead_of_estimates: boolean | null;
  version: string; // as num
}

const Reserves: React.FC<Props> = ({
  app,
  borrowerEntity,
  loadApp,
  dispatch,
  setReserveTotal,
}: Props) => {
  useEffect(() => {
    fetchReserves();
  }, []);

  const [reserves, setReserves] = useState<ReserveWorksheet>(null);
  const [formState, setFormState] = useState(null as any);
  const [formUpdates, setFormUpdates] = useState(null as any);
  const [reserveOverride, setReserveOverride] = useState<string>("");

  // UI
  const [overrideModalIsOpen, setOverrideModalIsOpen] = useState(false);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [deleteModalIsOpen, setDeleteModalIsOpen] = useState(false);
  const [editingMode, setEditingMode] = useState(EditingState.CREATE);
  const [invalidFields, setInvalidFields] = useState({});
  const [loadingState, setLoadingState] = useState(LoadingState.INACTIVE);
  const [errorMessage, setErrorMessage] = useState("");

  const validateForm = (name, form, validation) => {
    if (!validation) {
      return;
    }

    const copy = invalidFields;

    // clear this field's prior run validation
    delete copy[name];

    // and reset with new msg, if invalid
    const value = form[name];
    const invalidMsg = validation(value);
    if (invalidMsg !== "") {
      copy[name] = invalidMsg;
    }

    setInvalidFields(copy);
  };

  const handleChange = (name, value) => {
    // clear error msg
    setErrorMessage("");

    // just the updates, for persistence.
    setFormUpdates({ ...formUpdates, [name]: value });

    const updatedForm = { ...formState, [name]: value };

    // the entire form state, with updates, for display in the modal.
    setFormState(updatedForm);

    // validate after update, since input fields will display even invalid results.
    const fieldDef = modalFields.find(field => field.name === name);
    validateForm(name, updatedForm, fieldDef.validation);
  };

  const createReservesWorksheet = loanFileWs => {
    // 1. create it, and
    // 2. attach it to the loan_file worksheet

    // 1. creation
    const creationPayload = {
      application_id: app.id,
      worksheet_name: WS_NAME.RESERVES,
    };

    return APIClient.post(`/worksheets`, creationPayload).then(({ worksheet }) => {
      // 2. attach to loan_file ws

      const { id: reservesId } = worksheet;

      return updateLoanFileWorksheet(loanFileWs, [
        {
          name: "reserves",
          value: reservesId,
        },
      ]);
    });
  };

  const createReservesWorksheetIfNeeded = worksheets => {
    const reservesWs = getWsByName(worksheets, WS_NAME.RESERVES);
    if (reservesWs) {
      return Promise.resolve(worksheets);
    }

    const loanFileWs = getWsByName(worksheets, WS_NAME.LOAN_FILE);
    if (!loanFileWs) {
      // should never happen, as loan_file worksheet is created on loan application creation.
      throw new Error(`could not load loan_file worksheet on app ${app.id}`);
    }

    return createReservesWorksheet(loanFileWs).then(() => fetchWorksheets(app));
  };

  const fetchReserves = () => {
    setLoadingState(LoadingState.ACTIVE);

    return fetchWorksheets(app)
      .then(worksheets => createReservesWorksheetIfNeeded(worksheets))
      .then(worksheets => {
        const reservesWs = getWsByName(worksheets, WS_NAME.RESERVES);

        if (!reservesWs || !reservesWs.instance) {
          throw new Error(`could not load reserves worksheet on app ${app.id}`);
        }

        const { instance } = reservesWs;

        const {
          version: instanceVersion,
          computed_total,
          operator_override,
        } = instance as ReserveWorksheet;

        const reserves = instance.reserves?.map((reserve, i) => {
          const reserveWs = getWsById(worksheets, reserve);
          if (!reserveWs) {
            // this means:
            // the reserves ws lists a reserve ws that didn't get loaded.
            // a bad data situation. should never happen.
            throw new Error(
              `reserve worksheet '${reserve}' not found on reserves worksheet ${reservesWs.id}`,
            );
          }

          const { instance } = reserveWs;
          const { reserve_requirements } = instance;

          const reserveRequirements = reserve_requirements.map((req, j) => {
            const requirementWs = getWsById(worksheets, req);
            if (!requirementWs) {
              // this means:
              // the reserves ws lists a reserve requirement ws that didn't get loaded.
              // a bad data situation. just like above, should never happen.
              throw new Error(
                `reserve requirement worksheet for requirement with ID'${req}' not found on reserves worksheet ${reservesWs.id}`,
              );
            }

            const { instance: requirementInstance } = requirementWs;
            return {
              ...requirementInstance,
              array_index: j,
            };
          });

          // store original array index explicitly
          return {
            ...instance,
            reserve_requirements: reserveRequirements,
            array_index: i, // match ws field snake casing
          };
        });

        if (instance.operator_override) {
          setReserveOverride(instance.operator_override);
        }

        // Lift the reserves total out of worksheets to parent
        if (setReserveTotal) {
          setReserveTotal(operator_override ? operator_override : computed_total);
        }

        setReserves({
          id: reservesWs.id,
          version: instanceVersion,
          reserves,
          computed_total,
          operator_override,
        } as ReserveWorksheet);
        setLoadingState(LoadingState.FINISHED);
      })
      .catch(err => {
        setErrorMessage(err.meta.error_detail);
        setLoadingState(LoadingState.FINISHED);
      });
  };

  const submitReserveOverride = () => {
    const { id, version } = reserves;

    let overriddenReserveTotal = reserveOverride;

    // We have a `delete override` button that sets reserveOverride to null,
    // but if user just backspaces the value and attempts a saves, we should also remove
    if (reserveOverride === "") {
      overriddenReserveTotal = null;
    }

    const updates = { operator_override: overriddenReserveTotal };
    return updateWSValue(app.id, updates, id, version);
  };

  const createReserve = () => {
    // worksheet array: reserve requirement creation flow is:
    // 1. creation: create new array item (ie here, a "reserve")
    // 2. append: append it to the (parent worksheet) array (ie here, reserves)
    // 3. update: add values to that new array item (ie here, the "reserve" from step 1)

    // 1. creation
    const creationPayload = {
      application_id: app.id,
      worksheet_name: WS_NAME.RESERVES_FOR_PROPERTY,
    };

    let newReserveId;
    let newReserveVersion;

    return APIClient.post(`/worksheets`, creationPayload)
      .then(({ worksheet }) => {
        // 2. append

        newReserveId = worksheet.id;
        newReserveVersion = worksheet.version;

        const { id: reservesId, version: reservesVersion } = reserves;
        const appendPayload = {
          version: reservesVersion,
          updates: [
            {
              name: "reserves",
              value: newReserveId,
            },
          ],
        };

        return APIClient.post(`/worksheets/${reservesId}`, appendPayload);
      })
      .then(() => {
        // 3. update
        return updateReserve(newReserveId, newReserveVersion);
      });
  };

  const updateReserve = (id, version) => {
    if (!formUpdates) {
      // no updates -- nothing to do.
      return Promise.resolve(null);
    }

    return updateWSValue(app.id, prepareUpdatesForAPI(formUpdates), id, version);
  };

  const deleteReserve = () => {
    const { id: reservesId, version: reservesVersion } = reserves;
    const { array_index: index } = formState;

    return deleteWSValue(reservesId, WS_NAME.RESERVES, reservesVersion, index);
  };

  const importReserves = () => {
    const payload = {
      worksheet_id: reserves.id,
    };
    return APIClient.post(`/uw/${app.id}/worksheets/populate`, payload);
  };

  const borrowers = Object.keys(app.borrowers).map(be => Number(be));

  const modalFields: CarbonInputGenerationParams[] = [
    {
      name: "borrower_entity",
      type: "select",
      label: "Borrower Entity",
      options: ["", ...borrowers],
      formatForDisplay: getBorrowerEntityAsString,
      validation: notEmpty,
    },
    {
      name: "property",
      type: "text",
      label: "Property",
      placeholder: "42 Dunphy St",
    },
    {
      name: "pitia",
      type: "text",
      label: "PITIA",
      placeholder: "1500",
      beforeHandle: validCurrencyInputsOnly,
      validation: v => {
        const notEmptyMsg = notEmpty(v);
        if (notEmptyMsg !== "") {
          return notEmptyMsg;
        }

        // not a perfect $ input regex, so not extracting.
        // but good enough for this use case.
        const validPattern = /^-?\d*\.?\d{0,2}$/;
        if (!validPattern.test(v)) {
          return "Only include numbers, with up to 2 decimal places (eg -4.05, 1,000)";
        }

        return "";
      },
    },
    {
      name: "total_months",
      type: "text",
      label: "Total Months (Computed)",
      placeholder: "6",
      disabled: true,
    },
    {
      name: "total_amount_of_reserves_required",
      type: "text",
      label: "Total Amount of Reserves Required (Computed)",
      placeholder: "50000",
      disabled: true,
    },
  ];

  const prepareUpdatesForAPI = updates => {
    const preparedUpdates = { ...updates };
    Object.keys(updates).forEach(key => {
      const value = preparedUpdates[key];

      const fieldDef = modalFields.find(field => field.name === key);
      if (fieldDef.prepareForAPI) {
        preparedUpdates[key] = fieldDef.prepareForAPI(value);
      }
    });
    return preparedUpdates;
  };

  const overridden = reserves?.operator_override !== null;
  const overrideModalSubtitle = `The current reserve total is ${
    overridden
      ? formatCurrency(reserves?.operator_override, 2, 2)
      : formatCurrency(reserves?.computed_total, 2, 2)
  }. This value ${
    overridden ? "was set directly by an operator" : "is the computed total of all reserves."
  }`;

  const validateCurrency = (v: string): string => {
    // an empty string is a valid value
    if ("") {
      return "";
    }

    // not a perfect $ input regex, so not extracting.
    // but good enough for this use case.
    const validPattern = /^-?\d*\.?\d{0,2}$/;
    if (!validPattern.test(v)) {
      return "Only include numbers, with up to 2 decimal places (eg -4.05, 1,000)";
    }

    return "";
  };

  /**
   * Filters a list of reserves by the borrowerEntity prop
   * @param reserve A reserve, as loaded from the reserves worksheet
   * @returns
   */
  const filterReservesByBorrower = (reserve: Reserve) => {
    // If we're not filtering by borrower, return all
    if (!borrowerEntity) return true;

    // Unset borrower_entities should map to B1
    if (!reserve.borrower_entity) {
      return borrowerEntity === 1;
    }

    // Decompose entity and check if borrower entity is equal to the
    // lowest one
    const entity = new BorrowerEntityUtil(Number(reserve.borrower_entity));
    const decomposedEntity = entity.decomposed(entity);
    const lowestEntity = Math.min(...decomposedEntity);

    return borrowerEntity === lowestEntity;
  };

  const computeTotalReservesForBorrower = (reserves: Reserve[]): number => {
    return reserves.filter(filterReservesByBorrower).reduce((acc, cur) => {
      return acc + parseFloat(cur.total_amount_of_reserves_required);
    }, 0);
  };

  const baseTableTitle = (borrowerEntity: number, reserves: ReserveWorksheet) => {
    // Uncomment if we want to include the borrower entity in the title
    const heading = `Real Estate Owned: `;
    const totalValue = overridden
      ? formatCurrency(reserves?.operator_override, 2, 2)
      : borrowerEntity
      ? formatCurrency(computeTotalReservesForBorrower(reserves?.reserves), 2, 2)
      : formatCurrency(reserves?.computed_total, 2, 2);
    const hasBeenOverridden = overridden ? "(Overridden)" : "";

    return `${heading}${totalValue} ${hasBeenOverridden}`;
  };

  // Filter by the `borrowerEntity` if it's specified
  const filteredReserves = !borrowerEntity
    ? reserves?.reserves
    : reserves?.reserves.filter(filterReservesByBorrower);

  const invalidButtonStates = {
    isLoading: loadingState === LoadingState.ACTIVE,
    invalidCurrencyValueWasEntered: validateCurrency(reserveOverride) !== "",
    enteredValueIsUnchanged:
      overridden && reserves?.operator_override && reserveOverride === reserves.operator_override,
    blankInitialValue: !overridden && reserveOverride === "",
  };

  // Disable if any of the above conditions are true
  const shouldDisablePrimaryButton = Object.values(invalidButtonStates).some(Boolean);

  return (
    <>
      <Modal
        danger
        open={deleteModalIsOpen}
        modalHeading="Are you sure you'd like to delete this record?"
        primaryButtonText="Delete"
        secondaryButtonText="Cancel"
        onBlur={() => {
          setErrorMessage("");
          setDeleteModalIsOpen(false);
        }}
        onRequestClose={() => {
          setErrorMessage("");
          setDeleteModalIsOpen(false);
        }}
        onRequestSubmit={() => {
          setLoadingState(LoadingState.ACTIVE);

          deleteReserve()
            .then(() => {
              loadApp(dispatch, app.id);
            })
            .catch(err => {
              setErrorMessage(err.meta.error_detail);
              setLoadingState(LoadingState.FINISHED);
            });
        }}
      >
        {/* Loading spinner */}
        <Loading active={loadingState === LoadingState.ACTIVE} />

        {errorMessage.length > 0 && (
          <InlineNotification
            kind="error"
            notificationType="inline"
            role="alert"
            statusIconDescription="Error"
            subtitle={errorMessage}
            title="Error"
            onCloseButtonClick={() => {
              setErrorMessage("");
            }}
          />
        )}
      </Modal>

      <Modal
        open={overrideModalIsOpen}
        modalHeading="Override total reserves"
        primaryButtonText={
          // The "Save" button text should be context-dependent
          !overridden
            ? "Save overridden reserve value"
            : reserveOverride === ""
            ? "Remove overridden reserve value"
            : "Update overridden reserve value"
        }
        secondaryButtonText="Cancel"
        onBlur={() => setOverrideModalIsOpen(false)}
        onRequestClose={() => setOverrideModalIsOpen(false)}
        primaryButtonDisabled={shouldDisablePrimaryButton}
        danger={overridden && reserveOverride === ""}
        onRequestSubmit={() => {
          setLoadingState(LoadingState.ACTIVE);
          submitReserveOverride().then(() => {
            fetchReserves();
            setOverrideModalIsOpen(false);
            setLoadingState(LoadingState.FINISHED);
          });
        }}
      >
        <InlineNotification
          title={overridden ? "Alert" : "Note"}
          subtitle={overrideModalSubtitle}
          kind={overridden ? "warning" : "info"}
          notificationType="inline"
          role="info"
          lowContrast
          hideCloseButton
        />
        <TextInput
          id="reserve-override-value"
          labelText="Override value"
          value={reserveOverride || ""}
          invalid={validateCurrency(reserveOverride) !== ""}
          invalidText={validateCurrency(reserveOverride)}
          onChange={e => {
            const { value } = e.target;
            const commas = /,/g;
            const minusCommas = value.replace(commas, "");

            setReserveOverride(minusCommas);
          }}
        />
        <div style={{ marginBottom: "10px" }} />
      </Modal>

      <Modal
        open={modalIsOpen}
        modalHeading={editingMode === EditingState.CREATE ? "Add Reserve" : "Update Reserve"}
        primaryButtonText={editingMode === EditingState.CREATE ? "Save" : "Update"}
        secondaryButtonText="Cancel"
        primaryButtonDisabled={Object.keys(invalidFields).length > 0}
        onRequestClose={() => {
          setFormState(null);
          setFormUpdates(null);
          setErrorMessage("");
          setInvalidFields({});
          setModalIsOpen(false);
        }}
        onBlur={() => {
          setFormState(null);
          setFormUpdates(null);
          setErrorMessage("");
          setInvalidFields({});
          setModalIsOpen(false);
        }}
        onRequestSubmit={() => {
          // check all form fields, to confirm all are valid.
          const currentInvalidFields = modalFields.reduce((acc, fieldDef) => {
            if (!fieldDef.validation) {
              return acc;
            }
            const value = formState[fieldDef.name];
            const invalidMsg = fieldDef.validation(value);
            if (invalidMsg === "") {
              return acc;
            }
            return {
              ...acc,
              [fieldDef.name]: invalidMsg,
            };
          }, {});

          setInvalidFields(currentInvalidFields);

          if (Object.keys(currentInvalidFields).length > 0) {
            setErrorMessage("There are invalid fields. Please check again.");
            return;
          }

          if (editingMode === EditingState.CREATE) {
            setLoadingState(LoadingState.ACTIVE);

            createReserve()
              .then(() => {
                fetchReserves();
                setFormState(null);
                setFormUpdates(null);
                setModalIsOpen(false);
                setLoadingState(LoadingState.FINISHED);
              })
              .catch(err => {
                setErrorMessage(err.meta.error_detail);
                setLoadingState(LoadingState.FINISHED);
              });
          } else if (editingMode === EditingState.UPDATE) {
            setLoadingState(LoadingState.ACTIVE);

            const { id, version } = formState;

            updateReserve(id, version)
              .then(() => {
                fetchReserves();
                setFormState(null);
                setFormUpdates(null);
                setModalIsOpen(false);
                setLoadingState(LoadingState.FINISHED);
              })
              .catch(err => {
                setErrorMessage(err.meta.error_detail);
                setLoadingState(LoadingState.FINISHED);
              });
          }
        }}
      >
        {/* Loading spinner */}
        <Loading active={loadingState === LoadingState.ACTIVE} />

        {errorMessage.length > 0 && (
          <InlineNotification
            kind="error"
            notificationType="inline"
            role="alert"
            statusIconDescription="Error"
            subtitle={errorMessage}
            title="Error"
            onCloseButtonClick={() => {
              setErrorMessage("");
            }}
          />
        )}

        {formState && modalIsOpen && (
          <>
            <Form onSubmit={() => {}}>
              <FormGroup legendText="">
                {modalFields.map(fieldDef => (
                  <div key={`${fieldDef.name}-wrapper`}>
                    <Field
                      fieldDef={fieldDef}
                      value={formState[fieldDef.name]}
                      invalidMsg={invalidFields[fieldDef.name]}
                      handleChange={handleChange}
                    />
                    <div key={`${fieldDef.name}-margin-bottom`} style={{ height: "10px" }} />
                  </div>
                ))}
                <div style={{ height: "20px" }} />
              </FormGroup>

              <FormGroup legendText="Reserve Requirements">
                {editingMode === EditingState.CREATE ? (
                  <InlineNotification
                    kind="info"
                    notificationType="inline"
                    role="info"
                    statusIconDescription="Info"
                    subtitle="To add reserve requirements (base/payment shock/etc), first create the reserve, then edit the reserve."
                    title="Note"
                    onCloseButtonClick={() => {}}
                    hideCloseButton
                  />
                ) : (
                  <>
                    {formState.reserve_requirements.map(requirement => (
                      <ReserveRequirements
                        app={app}
                        loadApp={loadApp}
                        dispatch={dispatch}
                        fetchReserves={fetchReserves}
                        editingMode={
                          formState.reserve_requirements.map(r => r.id).includes(requirement.id)
                            ? EditingState.UPDATE
                            : EditingState.CREATE
                        }
                        setFormState={setFormState}
                        setFormUpdates={setFormUpdates}
                        reserveId={formState.id}
                        reserveVersion={formState.version}
                        reserveRequirement={requirement}
                      />
                    ))}
                    <ReserveRequirements
                      app={app}
                      loadApp={loadApp}
                      dispatch={dispatch}
                      editingMode={EditingState.CREATE}
                      fetchReserves={fetchReserves}
                      setFormState={setFormState}
                      setFormUpdates={setFormUpdates}
                      reserveId={formState.id}
                      reserveVersion={formState.version}
                      reserveRequirement={initialValues}
                    />
                  </>
                )}
              </FormGroup>
            </Form>
          </>
        )}
      </Modal>

      {reserves && (
        <>
          <div style={{ marginBottom: "10px" }}>
            {!borrowerEntity ||
              (borrowerEntity === 1 && (
                <>
                  <div className={styles.reservesContainer}>
                    <Button
                      renderIcon={DocumentDownload16}
                      onClick={() => {
                        setLoadingState(LoadingState.ACTIVE);

                        importReserves()
                          .then(() => {
                            fetchReserves();
                            setLoadingState(LoadingState.FINISHED);
                          })
                          .catch(err => {
                            setErrorMessage(err.meta.error_detail);
                            setLoadingState(LoadingState.FINISHED);
                          });
                      }}
                      kind="secondary"
                      style={{ whiteSpace: "nowrap", marginRight: "10px" }}
                    >
                      Import data from documents/defaults
                    </Button>
                    <div className={styles.override}>
                      <Button
                        onClick={() => {
                          setOverrideModalIsOpen(true);
                        }}
                        kind="tertiary"
                        renderIcon={Edit16}
                        tooltipPosition="right"
                        iconDescription="Edit reserves"
                      >
                        Override Reserves
                      </Button>
                    </div>
                  </div>
                </>
              ))}
            <div style={{ marginBottom: "20px" }} />
            <div className={overridden ? styles.overriddenTable : ""}>
              <BaseTable
                title={baseTableTitle(borrowerEntity, reserves)}
                headerData={makeHeaderData({
                  property: "Property Address",
                  total_months: "Months",
                  pitia: "PITIA",
                  total_amount_of_reserves_required: "Total Reserves",
                  reserve_requirements: "Reserve Requirements",
                })}
                rawRowData={filteredReserves}
                rowData={filteredReserves.map(reserve => {
                  const { reserve_requirements, pitia, total_amount_of_reserves_required } =
                    reserve;

                  return {
                    ...reserve,
                    pitia: formatCurrency(pitia, 2, 2),
                    total_amount_of_reserves_required: formatCurrency(
                      total_amount_of_reserves_required,
                      2,
                      2,
                    ),
                    reserve_requirements: reserve_requirements
                      .map(req => {
                        if (!req.reason || !req.number_of_months) {
                          return "-";
                        }
                        return `${req.reason}: ${req.number_of_months} months`;
                      })
                      .join(", "),
                  };
                })}
                collapseOverride={filteredReserves.length === 0}
                openModal={() => setModalIsOpen(true)}
                openDeleteModal={() => setDeleteModalIsOpen(true)}
                updateEditingMode={setEditingMode}
                initializeFormState={() => setFormState({})}
                updateFormState={data => setFormState(data)}
                emptyRowText="No reserves."
                disableBatch
              />
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default Reserves;
